<script setup lang="ts">
    import { ChevronDown, Check, Search } from 'lucide-vue-next';
    import {
        ComboboxAnchor,
        ComboboxContent,
        ComboboxInput,
        ComboboxItem,
        ComboboxItemIndicator,
        ComboboxLabel,
        ComboboxEmpty,
        ComboboxRoot,
        ComboboxTrigger,
        ComboboxViewport,
        ComboboxSeparator,
        ComboboxVirtualizer,
        useFilter,
    } from 'reka-ui';
    import { computed, ref, watch } from 'vue';
    import { useI18n } from 'vue-i18n';
    import { Button } from '@/components/ui/button';
    import { usePage } from '@inertiajs/vue3';
    import { type SharedData } from '@/types';
    import { useForm } from '@inertiajs/vue3';

    const { t } = useI18n();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const searchTerm = ref('');

    const form = useForm({
        selected_company_id: selectedCompanyId.value,
    });

    watch(selectedCompanyId, (newValue) => {
        form.selected_company_id = newValue;
    });

    const options = computed(() => page.props.auth?.companies);

    const { contains } = useFilter({ sensitivity: 'base' });

    const filteredOptions = computed(() => {
        const filtered = options.value.filter(
            (option) =>
                option.is_group ||
                option.is_seperator ||
                contains(option.label, searchTerm.value)
        );

        if(filtered[0].value === 'seperator-1') {
            filtered.shift();
        }

        return filtered;
    });

    const selectedOption = computed(() => {
        return options.value.find((opt) => opt.value === form.selected_company_id);
    });

    const getOptionLabel = computed(() => {
        if (!selectedOption.value || selectedOption.value.value === null)
            return t('companies.choose');
        return selectedOption.value.label;
    });

    const setSelectedCompany = () => {
        form.patch(route('v2.dashboard.selected.set'));
    };

    const open = ref(false);
</script>

<template>
    <ComboboxRoot
        v-model="form.selected_company_id"
        v-model:open="open"
        :multiple="false"
        @update:model-value="setSelectedCompany"
    >
        <ComboboxAnchor as-child>
            <ComboboxTrigger as-child>
                <Button
                    variant="ghost_primary"
                    size="xs"
                    width="full"
                    class="normal-case"
                >
                    {{ getOptionLabel }}
                    <ChevronDown class="size-4 cursor-pointer" />
                </Button>
            </ComboboxTrigger>
        </ComboboxAnchor>

        <ComboboxContent
            position="popper"
            align="end"
            :side-offset="4"
            class="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 w-xs origin-(--reka-combobox-content-transform-origin) overflow-y-auto rounded-md border bg-white shadow-md will-change-[opacity,transform]"
        >
            <div class="relative w-full max-w-sm items-center">
                <ComboboxInput
                    :display-value="() => ''"
                    v-model="searchTerm"
                    class="h-7.5 w-full rounded-none border-0 border-b pl-7.5 text-xs outline-none focus-visible:ring-0"
                    :placeholder="t('forms.search')"
                />
                <span
                    class="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center px-2"
                >
                    <Search class="text-faded-foreground size-3.5" />
                </span>
            </div>
            <ComboboxViewport class="max-h-80 p-1">
                <ComboboxVirtualizer
                    v-slot="{ option }"
                    :options="filteredOptions"
                    :estimate-size="24"
                    :text-content="(opt) => opt.label"
                >
                    <ComboboxLabel
                        v-if="option.is_group"
                        :key="option.label"
                        class="text-foreground/50 mx-2 w-full px-0 py-1.5 text-xs leading-none"
                    >
                        {{ option.label }}
                    </ComboboxLabel>
                    <div
                        v-else-if="option.is_seperator"
                        class="flex h-6 w-full items-center px-2"
                    >
                        <ComboboxSeparator
                            class="bg-border h-[1px] w-full"
                            :key="`${option.value}`"
                        />
                    </div>
                    <ComboboxItem
                        v-else
                        :value="option.value"
                        :key="option.value || 'default'"
                        class="data-[state=checked]:text-primary text-foreground data-[disabled]:text-muted-foreground data-[highlighted]:bg-primary/10 relative flex w-full cursor-pointer items-center gap-2 rounded-sm py-1.5 pr-2 pl-2 text-xs leading-none select-none data-[disabled]:pointer-events-none data-[highlighted]:outline-none data-[state=checked]:pr-8"
                    >
                        <span class="truncate">
                            {{ option.label }}
                        </span>
                        <span class="absolute right-2 flex size-3.5 items-center justify-center">
                            <ComboboxItemIndicator>
                                <Check class="text-primary size-3" />
                            </ComboboxItemIndicator>
                        </span>
                    </ComboboxItem>
                </ComboboxVirtualizer>
                <ComboboxEmpty>
                    <span class="text-muted-foreground p-2 text-xs">{{
                        t('forms.no_options')
                    }}</span>
                </ComboboxEmpty>
            </ComboboxViewport>
        </ComboboxContent>
    </ComboboxRoot>
</template>
